import { RecurrencePicker } from "@/components/calendars/RecurrencePicker.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { Pencil, Repeat } from "lucide-react";
import { useState } from "react";

type RecurrenceComponentData = Extract<ArtifactComponent, { type: "recurrence" }>;

export const RecurrenceBlock = ({ component }: { component: RecurrenceComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const [editing, setEditing] = useState(false);

  const save = async (rrule: string[] | undefined) => {
    try {
      await patchOne(component.editPath, rrule ?? null);
      setEditing(false);
    } catch (error) {
      console.error(error);
    }
  };

  if (!canEdit && !component.description) return null;

  return (
    <div className="flex flex-row gap-2 items-center">
      {editing ? (
        <div className="flex flex-row items-center gap-2">
          <RecurrencePicker
            value={component.rrule}
            onChange={save}
            eventDate={component.anchorMs ? new Date(component.anchorMs) : new Date()}
            triggerClassName="h-8 px-3 border border-input bg-background text-sm text-foreground rounded-md"
          />
          <Button
            onClick={() => setEditing(false)}
            variant="ghost"
            size="action"
            className="h-6 text-xs px-2 py-1 font-medium"
            data-statsig-name="artifact-view-cancel-recurrence"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <Repeat className="size-5" />
          <div className="text-sm @md:text-base text-foreground">
            {component.description
              ? component.description.charAt(0).toUpperCase() + component.description.slice(1)
              : "Does not repeat"}
          </div>
          {canEdit ? (
            <Button
              onClick={() => setEditing(true)}
              variant="ghost"
              size="action"
              className="h-6 w-6 p-0"
              aria-label="Edit recurrence"
              data-statsig-name="artifact-view-edit-recurrence"
            >
              <Pencil className="size-3" />
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
};
